import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {AIMessage, BaseMessage} from '@langchain/core/messages';
import {IsHumanPipe, RelativeTimePipe} from "@pipes/AIReports";
import {MarkdownComponent} from 'ngx-markdown';
import {StyleBoundaryComponent} from '@components/common/style-boundary/style-boundary.component';
import {ChatMessageArtifactComponent} from '../chat-message-artifact/chat-message-artifact.component';
import {INTERNAL_TOOL_NAMES} from 'src/app/common/models/ai-reports/shared/deep-agent-state';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

interface ToolCallView {
  id?: string;
  name: string;
  label: string;
  args: Record<string, unknown>;
}

@Component({
  selector: 'app-chat-message-bubble',
  standalone: true,
  imports: [CommonModule, IsHumanPipe, RelativeTimePipe, MarkdownComponent, StyleBoundaryComponent, ChatMessageArtifactComponent, TranslateModule],
  templateUrl: './chat-message-bubble.component.html',
  styleUrl: './chat-message-bubble.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageBubbleComponent {
  private readonly translate = inject(TranslateService);
  private readonly _langChange = toSignal(this.translate.onLangChange, { initialValue: null });

  message = input.required<BaseMessage>();

  /** Emitted when the user clicks "Try again" on a crashed message. The parent owns the retry. */
  readonly retryClicked = output<void>();

  readonly blocks = computed<any[]>(() => {
    const msg = this.message();
    const content = msg.content;

    if (typeof content === 'string') {
        if (!content.trim()) return [];
        return [{ type: 'text', text: content }];
    }

    if (Array.isArray(content)) {
        return content;
    }

    return [];
  });

  /**Finds the first text block in the message content. If there are more text blocks, ignore them. Tools can use this to have both user-facing text and hidden text in the same message.*/
  readonly text = computed(() => {
   const blocks = this.blocks();
   return blocks.find(block => block.type === 'text')?.text ?? undefined;
  });

  /** Only domain tool calls render a chip — internal deepagents framework calls are invisible plumbing. */
  readonly toolCalls = computed<ToolCallView[]>(() => {
    this._langChange();
    const msg = this.message();
    if (AIMessage.isInstance(msg) && msg.tool_calls) {
      return msg.tool_calls
        .filter(tc => !INTERNAL_TOOL_NAMES.has(tc.name))
        .map(tc => ({
          ...tc,
          label: this.resolveToolCallLabel(tc.name),
        }));
    }
    return [];
  });

  private resolveToolCallLabel(name: string): string {
    const key = `toolNames.${name}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : name.replace(/_/g, ' ');
  }

  readonly createdAt = computed(() => this.message().additional_kwargs?.['created_at']);

  readonly isCrashed = computed(() => {
    const msg = this.message();
    return AIMessage.isInstance(msg) && !!msg.additional_kwargs?.['is_agent_crashed'];
  });
}
